class KnitKitCalculator extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.dataset.initialized = 'true';
    this.addButton = this.querySelector('[data-knit-kit-add-to-cart]');
    this.feedback = this.querySelector('[data-knit-kit-feedback]');
    this.cartDrawer = document.querySelector('cart-drawer');
    this.currencyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'fi-FI', {
      style: 'currency',
      currency: this.dataset.currency || 'EUR',
    });

    this.addEventListener('change', (event) => {
      if (event.target.matches(
        'input[type="radio"][data-knit-kit-size], select[data-knit-kit-yarn-variant], input[type="checkbox"][data-knit-kit-accessory-toggle], select[data-knit-kit-accessory-variant]',
      )) {
        this.update();
      }
    });

    this.addEventListener('click', (event) => {
      const choiceButton = event.target.closest('[data-knit-kit-yarn-choice]');
      if (choiceButton && this.contains(choiceButton)) {
        this.selectYarnChoice(choiceButton);
        return;
      }

      const addButton = event.target.closest('[data-knit-kit-add-to-cart]');
      if (addButton && this.contains(addButton)) this.addToCart(addButton);
    });

    this.update();
  }

  getYarnPanel(slot, choiceId) {
    const panels = Array.from(this.querySelectorAll(`[data-knit-kit-yarn-panel="${slot}"]`));
    return panels.find((panel) => (choiceId ? panel.dataset.knitKitYarnChoiceId === choiceId : !panel.hidden)) || null;
  }

  getActiveYarnSelects() {
    return Array.from(this.querySelectorAll('select[data-knit-kit-yarn-variant]')).filter((select) => {
      const panel = select.closest('[data-knit-kit-yarn-panel]');
      return panel && !panel.hidden;
    });
  }

  selectYarnChoice(button) {
    const slot = button.dataset.knitKitYarnChoice;
    const choiceId = button.dataset.knitKitYarnChoiceId;
    const panel = this.getYarnPanel(slot, choiceId);
    if (!panel) return;

    this.querySelectorAll(`[data-knit-kit-yarn-choice="${slot}"]`).forEach((choice) => {
      const active = choice.dataset.knitKitYarnChoiceId === choiceId;
      choice.setAttribute('aria-pressed', String(active));
      choice.classList.toggle('is-active', active);
    });

    this.querySelectorAll(`[data-knit-kit-yarn-panel="${slot}"]`).forEach((choicePanel) => {
      choicePanel.hidden = choicePanel.dataset.knitKitYarnChoiceId !== choiceId;
    });

    this.update();
  }

  getYarnSelection(slot, size) {
    const panel = this.getYarnPanel(slot);
    const select = panel?.querySelector(`select[data-knit-kit-yarn-variant="${slot}"]`);
    const option = select?.selectedOptions[0];
    const skeinsValue = size?.getAttribute(`data-yarn-${slot}-skeins`);
    const gramsValue = size?.getAttribute(`data-yarn-${slot}-grams`);

    if (!panel || !select || !option || skeinsValue === null || skeinsValue === '') return null;

    const variantId = Number(option.value);
    const skeins = Number(skeinsValue);
    const grams = gramsValue === null || gramsValue === '' ? null : Number(gramsValue);
    const unitPrice = Number(option.dataset.price);

    if (!Number.isInteger(variantId) || variantId <= 0 || !Number.isInteger(skeins) || skeins <= 0) return null;
    if (!Number.isFinite(unitPrice) || (grams !== null && (!Number.isFinite(grams) || grams < 0))) return null;

    return {
      panel,
      option,
      variantId,
      skeins,
      grams,
      unitPrice,
      subtotal: unitPrice * skeins,
      yarnTitle: panel.dataset.yarnTitle || '',
      choiceId: panel.dataset.knitKitYarnChoiceId || '',
    };
  }

  getAccessorySelection(card) {
    const toggle = card.querySelector('[data-knit-kit-accessory-toggle]');
    const control = card.querySelector('[data-knit-kit-accessory-variant]');
    if (!toggle?.checked || !control) return null;

    const option = control instanceof HTMLSelectElement ? control.selectedOptions[0] : control;
    const variantId = Number(control.value);
    const unitPrice = Number(option?.dataset.price);
    if (!option || !Number.isInteger(variantId) || variantId <= 0 || !Number.isFinite(unitPrice)) return null;

    return {
      option,
      variantId,
      unitPrice,
      available: option.dataset.available === 'true' && !option.disabled,
      accessoryTitle: card.dataset.knitKitAccessoryTitle || '',
      variantTitle: option.dataset.title || '',
    };
  }

  updateAccessory(card) {
    const toggle = card.querySelector('[data-knit-kit-accessory-toggle]');
    const options = card.querySelector('[data-knit-kit-accessory-options]');
    const slot = card.dataset.knitKitAccessory;
    const item = this.querySelector(`[data-knit-kit-accessory-item="${slot}"]`);
    const selected = Boolean(toggle?.checked);

    if (options) options.hidden = !selected;
    if (item) item.hidden = !selected;
    card.classList.toggle('is-selected', selected);
    if (!selected) return null;

    const selection = this.getAccessorySelection(card);
    if (!selection) return null;

    const price = card.querySelector('[data-knit-kit-accessory-price]');
    const itemTitle = item?.querySelector('[data-knit-kit-accessory-item-title]');
    const itemDetail = item?.querySelector('[data-knit-kit-accessory-detail]');
    const subtotal = item?.querySelector('[data-knit-kit-accessory-subtotal]');
    const image = card.querySelector('[data-knit-kit-accessory-thumbnail]');
    const imageUrl = selection.option.dataset.image || card.dataset.knitKitAccessoryFallbackImage;
    const variantTitle = selection.variantTitle === 'Default Title' ? '' : selection.variantTitle;

    if (price) price.textContent = `(+${this.formatMoney(selection.unitPrice)})`;
    if (itemTitle) itemTitle.textContent = `Lisätarvike: ${selection.accessoryTitle}`;
    if (itemDetail) {
      itemDetail.textContent = variantTitle;
      itemDetail.hidden = !variantTitle;
    }
    if (subtotal) subtotal.textContent = this.formatMoney(selection.unitPrice);

    if (image instanceof HTMLImageElement && imageUrl) {
      image.src = imageUrl;
      image.alt = variantTitle ? `${selection.accessoryTitle}: ${variantTitle}` : selection.accessoryTitle;
    }

    return selection;
  }

  updateYarn(slot, selection) {
    const stock = selection.option.dataset.available === 'true'
      ? this.dataset.stockAvailable
      : this.dataset.stockUnavailable;
    const title = selection.option.dataset.title || selection.option.textContent.trim();
    const itemTitle = this.querySelector(`[data-knit-kit-yarn-item-title="${slot}"]`);
    const yarnTitle = this.querySelector(`[data-knit-kit-yarn-title="${slot}"]`);
    const rowCount = this.querySelector(`[data-knit-kit-skein-count="${slot}"]`);
    const headerCount = this.querySelector(`[data-knit-kit-skein-label="${slot}"]`);
    const unitPrice = this.querySelector(`[data-knit-kit-unit-price="${slot}"]`);
    const subtotal = this.querySelector(`[data-knit-kit-yarn-subtotal="${slot}"]`);
    const colorName = this.querySelector(`[data-knit-kit-color-name="${slot}"]`);
    const availability = selection.panel.querySelector(`[data-knit-kit-availability="${slot}"]`);
    const image = selection.panel.querySelector('[data-knit-kit-yarn-image]');
    const swatch = selection.panel.querySelector('[data-knit-kit-yarn-swatch]');
    const choicePrice = this.querySelector(
      `[data-knit-kit-yarn-choice="${slot}"][data-knit-kit-yarn-choice-id="${selection.choiceId}"] [data-knit-kit-choice-price]`,
    );

    if (headerCount) headerCount.textContent = `${selection.skeins} kerää`;
    if (rowCount) rowCount.textContent = `${selection.skeins} kerää`;
    if (unitPrice) unitPrice.textContent = this.formatMoney(selection.unitPrice);
    if (subtotal) subtotal.textContent = this.formatMoney(selection.subtotal);
    if (colorName) colorName.textContent = title;
    if (availability) availability.textContent = stock;
    if (yarnTitle) yarnTitle.textContent = selection.yarnTitle;
    if (itemTitle) itemTitle.textContent = `Lanka ${slot}: ${selection.yarnTitle}`;
    if (choicePrice) choicePrice.textContent = this.formatMoney(selection.unitPrice);

    if (image instanceof HTMLImageElement) {
      const imageUrl = selection.option.dataset.image;
      image.alt = `${selection.yarnTitle}: ${title}`;
      if (imageUrl) {
        image.src = imageUrl;
        image.hidden = false;
      } else {
        image.removeAttribute('src');
        image.hidden = true;
      }
      if (swatch) swatch.hidden = Boolean(imageUrl);
    }
  }

  update() {
    const selectedSize = this.querySelector('input[type="radio"][data-knit-kit-size]:checked');
    const yarnSelects = this.getActiveYarnSelects();
    const accessorySelections = Array.from(this.querySelectorAll('[data-knit-kit-accessory]'))
      .map((card) => this.updateAccessory(card))
      .filter(Boolean);
    const patternPrice = Number(this.dataset.patternPrice);
    const slotCount = Number(this.dataset.knitKitSlotCount);

    if (!selectedSize || !Number.isFinite(patternPrice) || !Number.isInteger(slotCount) || yarnSelects.length !== slotCount) {
      if (this.addButton) this.addButton.disabled = true;
      return;
    }

    const selections = yarnSelects.map((select) => this.getYarnSelection(select.dataset.knitKitYarnVariant, selectedSize));
    if (selections.some((selection) => !selection)) {
      if (this.addButton) this.addButton.disabled = true;
      return;
    }

    let total = patternPrice;
    const selectionParts = [];

    selections.forEach((selection, index) => {
      const slot = yarnSelects[index].dataset.knitKitYarnVariant;
      total += selection.subtotal;
      const grams = Number.isFinite(selection.grams) ? ` (${selection.grams} g)` : '';
      selectionParts.push(`Lanka ${slot}: ${selection.skeins} kerää${grams}`);
      this.updateYarn(slot, selection);
    });

    accessorySelections.forEach((selection) => {
      total += selection.unitPrice;
    });

    const selectionSummary = this.querySelector('[data-knit-kit-selected-quantity]');
    const totalOutput = this.querySelector('[data-knit-kit-total]');
    if (selectionSummary) selectionSummary.textContent = selectionParts.join(', ');
    if (totalOutput) totalOutput.textContent = this.formatMoney(total);
    this.setFeedback('');
    if (this.addButton) this.addButton.disabled = !this.canAddToCart();
  }

  canAddToCart() {
    if (this.dataset.patternAvailable !== 'true') return false;

    const selectedSize = this.querySelector('input[type="radio"][data-knit-kit-size]:checked');
    const yarnSelects = this.getActiveYarnSelects();
    const slotCount = Number(this.dataset.knitKitSlotCount);
    if (!selectedSize || !Number.isInteger(slotCount) || slotCount < 1 || yarnSelects.length !== slotCount) return false;

    const yarnsAvailable = yarnSelects.every((select) => {
      const option = select.selectedOptions[0];
      const selection = this.getYarnSelection(select.dataset.knitKitYarnVariant, selectedSize);
      return Boolean(option && !option.disabled && option.dataset.available === 'true' && selection);
    });
    const accessoriesAvailable = Array.from(this.querySelectorAll('[data-knit-kit-accessory]')).every((card) => {
      const toggle = card.querySelector('[data-knit-kit-accessory-toggle]');
      if (!toggle?.checked) return true;
      const selection = this.getAccessorySelection(card);
      return Boolean(selection && selection.available);
    });

    return yarnsAvailable && accessoriesAvailable;
  }

  formatMoney(cents) {
    return this.currencyFormatter.format(cents / 100);
  }

  setFeedback(message, status) {
    if (!this.feedback) return;
    this.feedback.textContent = message;
    if (status) this.feedback.dataset.status = status;
    else this.feedback.removeAttribute('data-status');
  }

  async addToCart(button) {
    if (this.isAdding) return;

    if (!this.canAddToCart()) {
      this.setFeedback('Valitse jokaiselle langalle saatavilla oleva väri ennen lisäämistä.', 'error');
      return;
    }

    const selectedSize = this.querySelector('input[type="radio"][data-knit-kit-size]:checked');
    const patternForm = document.getElementById(this.dataset.patternFormId);
    const patternVariantId = patternForm?.querySelector('[name="id"]')?.value || this.dataset.patternVariantId;
    const patternId = Number(patternVariantId);
    const yarnSelects = this.getActiveYarnSelects();
    const items = [{ id: patternId, quantity: 1 }];

    if (!Number.isInteger(patternId) || patternId <= 0) {
      this.setFeedback('Neuleohjeen lisääminen ei onnistunut. Yritä uudelleen.', 'error');
      return;
    }

    for (const select of yarnSelects) {
      const selection = this.getYarnSelection(select.dataset.knitKitYarnVariant, selectedSize);
      if (!selection || selection.option.disabled || selection.option.dataset.available !== 'true') {
        this.setFeedback('Valitse jokaiselle langalle saatavilla oleva väri ennen lisäämistä.', 'error');
        return;
      }
      items.push({ id: selection.variantId, quantity: selection.skeins });
    }

    for (const card of this.querySelectorAll('[data-knit-kit-accessory]')) {
      const toggle = card.querySelector('[data-knit-kit-accessory-toggle]');
      if (!toggle?.checked) continue;

      const selection = this.getAccessorySelection(card);
      if (!selection || !selection.available) {
        this.setFeedback('Valitse lisätarvikkeelle saatavilla oleva vaihtoehto ennen lisäämistä.', 'error');
        return;
      }

      items.push({ id: selection.variantId, quantity: 1 });
    }

    this.isAdding = true;
    button.disabled = true;
    button.classList.add('loading');
    button.setAttribute('aria-busy', 'true');
    button.querySelector('.loading__spinner')?.classList.remove('hidden');
    this.setFeedback('Lisätään neulesettiä ostoskoriin.');
    this.cartDrawer?.setActiveElement(button);

    try {
      const addUrl = new URL(this.dataset.cartAddUrl, window.location.origin);
      if (!addUrl.pathname.endsWith('.js')) addUrl.pathname += '.js';

      const sectionsToRender = this.cartDrawer?.getSectionsToRender?.() || [];
      const sectionIds = sectionsToRender.map((section) => section.id);
      if (sectionIds.length) {
        addUrl.searchParams.set('sections', sectionIds.join(','));
        addUrl.searchParams.set('sections_url', window.location.pathname);
      }

      const response = await fetch(addUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Neulesetin lisääminen ei onnistunut. Yritä uudelleen.');
      }

      this.setFeedback('Neulesetti lisättiin ostoskoriin.');

      if (this.cartDrawer) {
        const hasRenderedSections = sectionIds.length > 0
          && sectionIds.every((sectionId) => typeof result.sections?.[sectionId] === 'string');

        if (hasRenderedSections) {
          try {
            this.cartDrawer.classList.remove('is-empty');
            this.cartDrawer.renderContents(result);
          } catch (error) {
            this.setFeedback('Neulesetti lisättiin ostoskoriin. Avaa ostoskori nähdäksesi tuotteet ja summan.');
          }
        } else {
          this.setFeedback('Neulesetti lisättiin ostoskoriin. Avaa ostoskori nähdäksesi tuotteet ja summan.');
        }
      }
    } catch (error) {
      this.setFeedback(error.message || 'Neulesetin lisääminen ei onnistunut. Yritä uudelleen.', 'error');
    } finally {
      this.isAdding = false;
      button.classList.remove('loading');
      button.removeAttribute('aria-busy');
      button.querySelector('.loading__spinner')?.classList.add('hidden');
      button.disabled = !this.canAddToCart();
    }
  }
}

if (!customElements.get('knit-kit-calculator')) {
  customElements.define('knit-kit-calculator', KnitKitCalculator);
}
